import React from 'react';
import { isDerivedTextFallback } from './photoTextFallback';

import { PhotoExtractionStatus } from '../../components/survey/PhotoExtractionStatus';
import { UI_TEXT } from '../../config/ui-text';
import type { ServerImageLike } from './photoUploadSessionSync';

const P = UI_TEXT.HUMAN_SURVEY.PHOTO;
const API_URL = import.meta.env.VITE_API_URL || '';

/** Same rule as SurveySession/InfoScreenDisplay's own copies: only `/api/`-prefixed paths are relative to us. */
const getFullMediaUrl = (url: string): string => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  if (url.startsWith('/api/')) return `${API_URL}${url}`;
  return url;
};

interface PhotoAnswerProduct {
  name: string;
  brand?: string;
}

interface PhotoAnswerImage extends Omit<ServerImageLike, 'extracted_products'> {
  url: string;
  extracted_products?: PhotoAnswerProduct[];
  text_input?: string;
}

interface PhotoAnswerValue {
  images?: PhotoAnswerImage[];
  text_fallback?: string;
}

interface PhotoAnswerImagesProps {
  value: PhotoAnswerValue | null | undefined;
}

const productLabel = (product: PhotoAnswerProduct): string =>
  product.brand ? `${product.name} (${product.brand})` : product.name;

/**
 * The researcher's per-image photo answer view — image preview, extraction status, product
 * chips, and any text the respondent typed. Shared by both places SessionResponses renders a
 * `photo_upload` answer (the single-item JSX path and the conversation-flow dedicated path),
 * so a fix here fixes both instead of two independently-drifting copies.
 *
 * Three states per image, in priority order:
 *  1. `extracted_products` non-empty — a green count + name/brand chips, regardless of status.
 *  2. `extraction_status === 'failed'` (with or without a `rejection_reason`) — the shared
 *     `PhotoExtractionStatus` note (researcher-facing "AI note" label).
 *  3. Anything else — `processing`, `uploading`, `completed` with no products, or
 *     a missing status — is not a verdict, so it renders a neutral "Not analysed" badge, never
 *     the old fabricated "No products detected" / "Not detected" text.
 *
 * `text_input` — whatever the respondent typed for this image — is independent of all three
 * states and always rendered when present. A respondent can type a fallback answer regardless of
 * what the AI decided (or whether it ever got to a decision), so gating it on `hasFailed` hid
 * their words on any non-`failed` status, which the renderer this replaces did not do.
 */
const PhotoAnswerImages: React.FC<PhotoAnswerImagesProps> = ({ value }) => {
  const images = value?.images ?? [];
  const textFallback = isDerivedTextFallback(value?.text_fallback, images)
    ? undefined
    : value?.text_fallback;

  return (
    <div className="space-y-4">
      {images.map((img, idx) => {
        const products = img.extracted_products ?? [];
        const hasProducts = products.length > 0;
        const hasFailed = img.extraction_status === 'failed';
        const textInput = img.text_input || '';

        return (
          <div
            key={img.s3_key ?? idx}
            className="bg-white border border-gray-200 rounded-lg overflow-hidden"
          >
            <div className="bg-gray-100 px-3 py-2 border-b border-gray-200 flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Image {idx + 1}</span>
              {hasProducts && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                  {products.length} product{products.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>

            <div className="aspect-video bg-gray-100">
              <img
                src={getFullMediaUrl(img.url)}
                alt={`Photo ${idx + 1}`}
                className="w-full h-full object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            </div>

            {hasProducts && (
              <div className="p-3 border-t border-gray-100">
                <div className="flex flex-wrap gap-1.5">
                  {products.map((product, pIdx) => (
                    <span
                      key={pIdx}
                      className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-emerald-50 text-emerald-700 border border-emerald-200"
                    >
                      {productLabel(product)}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {!hasProducts && (
              <div
                className={`border-t ${hasFailed ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-200'}`}
              >
                <div className="p-3">
                  {hasFailed ? (
                    <PhotoExtractionStatus image={img} noteLabel={P.AI_NOTE} />
                  ) : (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-gray-200 text-gray-600">
                      {P.NOT_ANALYSED}
                    </span>
                  )}
                </div>
              </div>
            )}

            {textInput && (
              <div className="p-3 border-t border-gray-200 bg-white">
                <p className="text-sm text-gray-600 font-medium mb-0.5">User input:</p>
                <p className="text-sm text-gray-900">{textInput}</p>
              </div>
            )}
          </div>
        );
      })}

      {images.length === 0 && <p className="text-gray-500 text-sm">No response</p>}

      {textFallback && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
          <p className="text-xs font-medium text-gray-600 mb-0.5">{P.RESPONDENT_TEXT}</p>
          <p className="text-sm text-gray-900 whitespace-pre-wrap">{textFallback}</p>
        </div>
      )}
    </div>
  );
};

export default PhotoAnswerImages;
